import {z as zod} from "zod";
import type {
    Integer,
    Iso8601DateTime,
    NonEmptyString,
    Uuid,
} from "@vesta/common-type-definitions/typeDefinitions";
import {
    booleanStringZodValidator,
    fileZodValidator,
    iso8601DateTimeZodValidator,
    jsonZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";

type DataResult<T> = {
    data: T;
    success: true;
};

type ErrorResult<T = Error> = {
    error: T;
    success: false;
};

export type Result<T, U = Error> = DataResult<T> | ErrorResult<U>;

function dataResult<T>(data: T): DataResult<T> {
    return {
        data,
        success: true,
    };
}

function errorResult<T = Error>(error: T): ErrorResult<T> {
    return {
        error,
        success: false,
    };
}

function resultFromZodParse<T>(schema: zod.ZodType<T>, input: unknown): Result<T> {
    const result = schema.safeParse(input);
    if (result.success !== true) {
        return errorResult(result.error);
    }

    return dataResult(result.data);
}

/**
 * @deprecated Use iso8601DateTimeZodValidator instead
 */
export function getIso8601DateTimeFromUnknown(
    input: unknown,
): Result<Iso8601DateTime> {
    return resultFromZodParse(iso8601DateTimeZodValidator, input);
}

/**
 * @deprecated Use uuidZodValidator instead
 */
export function getUuidFromUnknown(input: unknown): Result<Uuid> {
    return resultFromZodParse(uuidZodValidator, input);
}

/**
 * @deprecated Use zod.enum instead
 */
export function getEnumValueFromUnknown<T>(
    input: unknown,
    // TODO: Figure out type
    enumObject: Record<string | symbol | number, T>,
): Result<T> {
    return resultFromZodParse(
        zod.custom<T>((value) => Object.values(enumObject).includes(value as T), {
            message: `Invalid enum value ${String(input)}`,
        }),
        input,
    );
}

/**
 * @deprecated Use zod.string instead
 */
export function getStringFromUnknown(input: unknown): Result<string> {
    return resultFromZodParse(zod.string(), input);
}

export function getNonEmptyStringFromUnknown(
    input: unknown,
): Result<NonEmptyString> {
    return resultFromZodParse(
        zod.string().min(1).transform((value) => value as NonEmptyString),
        input,
    );
}

// Trims the string and rejects empty strings.
export function getSanitizedStringFromUnknown(
    input: unknown,
): Result<NonEmptyString> {
    return resultFromZodParse(
        zod.string().trim().min(1).transform((value) => value as NonEmptyString),
        input,
    );
}

// TODO: Accept a number or string, and parse accordingly
export function getIntegerFromUnknown(input: unknown): Result<Integer> {
    return resultFromZodParse(
        zod
            .string()
            .min(1)
            .transform((value) => Number(value))
            .pipe(zod.number().int())
            .transform((value) => value as Integer),
        input,
    );
}

// TODO: Accept a number or string, and parse accordingly
/**
 * @deprecated Use zod.number() instead
 */
export function getNumberFromUnknown(input: unknown): Result<number> {
    return resultFromZodParse(
        zod.union([
            zod.number(),
            zod
                .string()
                .min(1)
                .transform((value) => Number(value))
                .pipe(zod.number()),
        ]),
        input,
    );
}

export function getErrorFromUnknown(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }

    return new Error(String(error));
}

/**
 * @deprecated Use zod.object() instead
 */
export function getObjectFromUnknown(input: unknown): Result<unknown> {
    return resultFromZodParse(jsonZodValidator, input);
}

/**
 * @deprecated
 */
// TODO: Accept a boolean or string, and parse accordingly
export function getBooleanFromUnknown(input: unknown): Result<boolean> {
    return resultFromZodParse(zod.union([zod.boolean(), booleanStringZodValidator]), input);
}

export function getFileFromUnknown(input: unknown): Result<File> {
    return resultFromZodParse(fileZodValidator, input);
}
